import React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { CardDetailFragment, ChangeCardAliasDocument } from 'generated/resources/graphql'

import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SidebarSection } from '../_SidebarSection'

type ChangeAliasFormValues = {
  id: string
  alias: string
}

const useChangeAlias = (card: CardDetailFragment) => {
  const [changeAlias, { loading: submitting }] = useMutation(ChangeCardAliasDocument, {
    extractorFn: (data) => data.bankingMutations.changeAliasBankingCard,
  })

  const form = useForm<ChangeAliasFormValues>({
    defaultValues: {
      id: card.id + '',
      alias: card.alias ?? '',
    },
    validate: (data) => {
      const errors: FormErrors<ChangeAliasFormValues> = {}
      if (!data.alias) {
        errors.alias = i18n.t('common.validations.empty')
      }
      return errors
    },
    onSubmit: async (values) => {
      const result = await changeAlias({ variables: values })
      const handled = handleResult(result, (errors) => {
        form.setErrors({
          alias: errors['alias'],
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
      return handled
    },
  })

  const readonly = !card._policies?.changeAlias?.can
  const submitDisabled =
    readonly || !form.dirty || submitting || form.isSubmitting || !!form.errors.alias
  const onSubmit = () => form.handleSubmit()
  const onAliasChange = (value: string) => form.setFieldValue('alias', value)

  return { form, submitDisabled, onSubmit, onAliasChange, readonly }
}

export const CardAlias: React.FC<{ card: CardDetailFragment }> = ({ card }) => {
  const { form, submitDisabled, onSubmit, onAliasChange, readonly } = useChangeAlias(card)

  return (
    <SidebarSection>
      <Box width="full" paddingBottom="s24">
        <Text.Normal weight="semibold" size="300" color="textPrimary">
          {i18n.t('banking.cards.card_alias')}
        </Text.Normal>
      </Box>
      <Box gap="s24" width="full" paddingBottom="s12">
        <TextField
          label={i18n.t('banking.cards.alias')}
          onChange={onAliasChange}
          value={form.values.alias}
          error={form.touched.alias ? form.errors.alias : undefined}
          readonly={readonly}
        />
      </Box>
      {!readonly && (
        <Box width="full" flexDirection="rowReverse">
          <Button
            type="secondary"
            onClick={onSubmit}
            disabled={submitDisabled}
            aria-label={i18n.t('common.update')}
          >
            {i18n.t('common.update')}
          </Button>
        </Box>
      )}
    </SidebarSection>
  )
}
